import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subscription, combineLatest } from 'rxjs';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { AppState } from '@app/Store/reducers';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { LoggerClass } from '@app/Shared/decorator.ts';
import { iParsedEntity, iProperty } from '@app/Shared/interfaces/iEntityParsed';
import * as DashboardActions from '@app/Store/actions/dashboard-actions';
import { pluralize } from '@app/Shared/util/stringManipulation';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { ActionsSubject } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { selectColumnConfigForEntity } from '@app/Store/features/column-config.feature';

// Column configuration interface
interface ColumnConfig {
    field: string;
    visible: boolean;
    sortable: boolean;
    defaultSort?: 'asc' | 'desc';
    sortIndex?: number;
    width?: number;
    minWidth?: number;
    pinned?: 'left' | 'right';
    headerName?: string;
    tooltip?: string;
}

@Component({
    selector: 'app-edit-row-tab',
    templateUrl: './edit-row-tab.component.html',
    styleUrls: ['./edit-row-tab.component.scss'],
})
@LoggerClass
export class EditRowTabComponent implements OnInit, OnDestroy {
    public selectedEntity: iParsedEntity | null = null;
    public rowData: any = null;
    public editedData: any = null;
    public isCompactView: boolean = true; // Default to compact
    public hasChanges: boolean = false;
    public isSaving: boolean = false;
    public columnConfig: ColumnConfig[] = [];
    public isEditRowTabActive: boolean = false;
    private subscription: Subscription = new Subscription();
    private originalData: any = null;

    constructor(
        private store: Store<AppState>,
        private cdr: ChangeDetectorRef,
        private alertService: AlertService,
        private actions$: ActionsSubject,
    ) {}
    ngOnInit(): void {
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectTabChange), distinctUntilChanged()).subscribe((selectedTab) => {
                this.isEditRowTabActive = selectedTab === 'EditRow';

                // Clear data when switching away from EditRow tab
                if (!this.isEditRowTabActive && this.rowData) {
                    this.clearRowData();
                }
            }),
        );
        // Subscribe to selected entity
        this.subscription.add(
            this.store
                .pipe(
                    select(dashboardFeatureSelectors.selectSelectedEntity),
                    distinctUntilChanged((prev, curr) => prev?.entityName === curr?.entityName),
                )
                .subscribe((entity) => {
                    this.selectedEntity = entity;
                    // If we have row data but entity changed, clear the data
                    if (this.rowData && entity && this.rowData[entity.key] === undefined) {
                        this.clearRowData();
                    }

                    // Subscribe to column config for this entity
                    if (entity) {
                        this.subscription.add(
                            this.store.select(selectColumnConfigForEntity(entity.entityName)).subscribe((columnConfig: ColumnConfig[]) => {
                                this.columnConfig = columnConfig || [];
                                this.cdr.detectChanges();
                            }),
                        );
                    }
                }),
        );

        this.subscription.add(
            combineLatest([
                this.store.pipe(select(dashboardFeatureSelectors.selectTabChange)),
                this.store.pipe(select(dashboardFeatureSelectors.selectSelectedRecords)),
                this.store.pipe(select(dashboardFeatureSelectors.selectSelectedEntity)),
            ])
                .pipe(
                    distinctUntilChanged((prev, curr) => {
                        // Compare tab, records, and entity
                        const [prevTab, prevRecords, prevEntity] = prev;
                        const [currTab, currRecords, currEntity] = curr;

                        // If tab changed, always emit
                        if (prevTab !== currTab) return false;

                        // If entity changed, always emit
                        if (prevEntity?.entityName !== currEntity?.entityName) return false;

                        // If records length changed, always emit
                        if (!prevRecords || !currRecords || prevRecords.length !== currRecords.length) return false;

                        // If no records, don't emit
                        if (prevRecords.length === 0) return true;

                        // Compare record keys
                        const prevKey = prevRecords[0]?.data?.[prevEntity?.key];
                        const currKey = currRecords[0]?.data?.[currEntity?.key];
                        return prevKey === currKey;
                    }),
                    filter(([selectedTab, records, entity]) => {
                        // Only proceed if EditRow tab is active, records exist, and entity exists
                        return selectedTab === 'EditRow' && records && records.length > 0 && entity !== null;
                    }),
                )
                .subscribe(([selectedTab, records, entity]) => {
                    if (records && records.length > 0 && entity) {
                        const selectedRecord = records[0].data;
                        // Only update if we don't have row data or if it's a different row
                        if (!this.rowData || this.rowData[entity.key] !== selectedRecord[entity.key]) {
                            this.loadRowData(selectedRecord);
                        }
                    }
                }),
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
    private loadRowData(data: any): void {
        // Create deep copy to avoid read-only issues
        this.originalData = JSON.parse(JSON.stringify(data));
        this.rowData = JSON.parse(JSON.stringify(data));
        this.editedData = JSON.parse(JSON.stringify(data));
        this.hasChanges = false;
        this.cdr.detectChanges();
    }

    public clearRowData(): void {
        this.rowData = null;
        this.editedData = null;
        this.originalData = null;
        this.hasChanges = false;
        this.cdr.detectChanges();
    }

    public toggleView(): void {
        this.isCompactView = !this.isCompactView;
        this.cdr.detectChanges();
    }

    public setNormalView(): void {
        this.isCompactView = false;
        this.cdr.detectChanges();
    }

    public setCompactView(): void {
        this.isCompactView = true;
        this.cdr.detectChanges();
    }

    public onFieldChange(field: string, value: any): void {
        if (this.editedData) {
            // Get the property metadata to convert value to correct type
            const property = this.getPropertyByName(field);
            if (property) {
                this.editedData[field] = this.convertValueByType(value, property.type, property.nullable);
            } else {
                // If property not found, keep value as-is
                this.editedData[field] = value;
            }
            this.checkForChanges();
        }
    }

    private getPropertyByName(fieldName: string): iProperty | undefined {
        if (!this.selectedEntity) return undefined;
        return this.selectedEntity.properties.find((prop) => prop.name === fieldName);
    }

    private convertValueByType(value: any, edmType: string, nullable: boolean): any {
        // Handle null/empty values for nullable fields
        if ((value === null || value === undefined || value === '') && nullable) {
            return null;
        }

        // Handle empty strings for non-nullable fields - return empty string or appropriate default
        if (value === '' && !nullable) {
            if (this.isNumberType(edmType)) {
                return 0;
            }
            if (this.isBooleanType(edmType)) {
                return false;
            }
            return '';
        }

        // Convert based on EDM type
        if (this.isNumberType(edmType)) {
            // Convert to number
            if (edmType === 'Edm.Int32' || edmType === 'Edm.Int16' || edmType === 'Edm.Byte') {
                const numValue = parseInt(value, 10);
                return isNaN(numValue) ? (nullable ? null : 0) : numValue;
            }
            if (edmType === 'Edm.Int64') {
                // Int64 might need BigInt in some cases, but usually number is fine
                const numValue = parseInt(value, 10);
                return isNaN(numValue) ? (nullable ? null : 0) : numValue;
            }
            if (edmType === 'Edm.Decimal' || edmType === 'Edm.Double' || edmType === 'Edm.Single') {
                const numValue = parseFloat(value);
                return isNaN(numValue) ? (nullable ? null : 0) : numValue;
            }
            // Default number conversion
            const numValue = parseFloat(value);
            return isNaN(numValue) ? (nullable ? null : 0) : numValue;
        }

        if (this.isBooleanType(edmType)) {
            // Convert to boolean
            if (typeof value === 'boolean') {
                return value;
            }
            if (typeof value === 'string') {
                return value === 'true' || value === 'True' || value === '1';
            }
            return Boolean(value);
        }

        if (this.isDateType(edmType)) {
            // Check if it's date-only (Edm.Date) or datetime (Edm.DateTimeOffset, Edm.DateTime, etc.)
            // Edm.Date = date-only (YYYY-MM-DD)
            // Edm.DateTimeOffset, Edm.DateTime = datetime with time (ISO 8601 format)
            const isDateOnly = edmType === 'Edm.Date' || edmType === 'Date';

            if (value instanceof Date) {
                if (isDateOnly) {
                    // For date-only, return YYYY-MM-DD format
                    const year = value.getFullYear();
                    const month = String(value.getMonth() + 1).padStart(2, '0');
                    const day = String(value.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                } else {
                    // For datetime, return full ISO 8601 string
                    return value.toISOString();
                }
            }

            if (typeof value === 'string' && value) {
                // Parse string to Date object first
                // Handles datetime-local format (YYYY-MM-DDTHH:mm) and other ISO date formats
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    if (isDateOnly) {
                        // For date-only, return YYYY-MM-DD format
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    } else {
                        // For datetime, return full ISO 8601 string
                        return date.toISOString();
                    }
                }
            }

            // Return null for nullable fields, or current date for non-nullable
            if (nullable) {
                return null;
            } else {
                const now = new Date();
                if (isDateOnly) {
                    const year = now.getFullYear();
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const day = String(now.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                } else {
                    return now.toISOString();
                }
            }
        }

        // String type or unknown type - keep as string or convert to string
        if (value === null || value === undefined) {
            return nullable ? null : '';
        }
        return String(value);
    }

    private checkForChanges(): void {
        if (!this.originalData || !this.editedData || !this.selectedEntity) {
            this.hasChanges = false;
            return;
        }

        const editablePropNames = new Set(this.getEditableProperties().map((p) => p.name));
        this.hasChanges = Object.keys(this.editedData).some((key) => {
            if (!editablePropNames.has(key)) return false;
            return this.editedData[key] !== this.originalData[key];
        });

        this.cdr.detectChanges();
    }
    public isIdField(field: string): boolean {
        return this.selectedEntity && this.selectedEntity.key === field;
    }

    public getReadOnlyProperties(): iProperty[] {
        if (!this.selectedEntity) return [];
        return this.selectedEntity.properties.filter((prop) => {
            if (prop.readOnly !== undefined) return prop.readOnly;
            return prop.name === this.selectedEntity!.key;
        });
    }

    public getEditableProperties(): iProperty[] {
        if (!this.selectedEntity) return [];

        const editableProps = this.selectedEntity.properties.filter((prop) => {
            if (prop.readOnly !== undefined) return !prop.readOnly;
            return prop.name !== this.selectedEntity!.key;
        });

        // If we have column config, order by config order
        if (this.columnConfig.length > 0) {
            // Create a map for quick lookup
            const propertyMap = new Map(editableProps.map((prop) => [prop.name, prop]));
            const orderedSet = new Set<string>();

            // Get properties in the order specified by column config
            const orderedProps: iProperty[] = [];

            // Add properties in column config order (respect config order)
            this.columnConfig.forEach((config) => {
                if (propertyMap.has(config.field)) {
                    orderedProps.push(propertyMap.get(config.field)!);
                    orderedSet.add(config.field);
                }
            });

            // Add any properties not in config at the end (in their original order)
            editableProps.forEach((prop) => {
                if (!orderedSet.has(prop.name)) {
                    orderedProps.push(prop);
                }
            });

            return orderedProps;
        }

        // If no column config, return in original order
        return editableProps;
    }

    public getDisplayName(propertyName: string): string {
        if (!this.columnConfig.length) return propertyName;
        const config = this.columnConfig.find((col) => col.field === propertyName);
        return config?.headerName || propertyName;
    }

    public isDateType(type: string): boolean {
        return type && (type.includes('DateTime') || type.includes('Date') || type.includes('Time'));
    }

    public isDateOnly(type: string): boolean {
        return type === 'Edm.Date' || type === 'Date';
    }

    public isBooleanType(type: string): boolean {
        return type && type.includes('Boolean');
    }

    public isNumberType(type: string): boolean {
        return type && (type.includes('Int') || type.includes('Decimal') || type.includes('Double') || type.includes('Single') || type.includes('Byte'));
    }

    public formatDateForInput(dateValue: any, edmType?: string): string {
        if (!dateValue) return '';
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            // For date-only (Edm.Date), return YYYY-MM-DD
            if (edmType && this.isDateOnly(edmType)) {
                return `${year}-${month}-${day}`;
            }

            // For datetime, format as datetime-local input expects: YYYY-MM-DDTHH:mm
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch {
            return '';
        }
    }

    public cancel(): void {
        if (this.hasChanges) {
            this.alertService.confirm(
                'You have unsaved changes. Are you sure you want to cancel?',
                'Confirm Cancel',
                () => {
                    this.editedData = JSON.parse(JSON.stringify(this.originalData));
                    this.hasChanges = false;
                    this.cdr.detectChanges();
                },
                () => {},
            );
        } else {
            this.clearRowData();
        }
    }

    public save(): void {
        if (!this.hasChanges || !this.selectedEntity || !this.editedData) {
            return;
        }

        const entityName = pluralize(this.selectedEntity.entityName);
        const keyValue = this.editedData[this.selectedEntity.key];

        if (keyValue === undefined || keyValue === null) {
            this.alertService.notify(AlertType.Error, 'Cannot save: key value is missing', 'Save Error');
            return;
        }

        const editablePropNames = new Set(this.getEditableProperties().map((p) => p.name));
        const payload = Object.keys(this.editedData)
            .filter((k) => editablePropNames.has(k))
            .reduce((acc, k) => ({ ...acc, [k]: this.editedData[k] }), {} as Record<string, any>);

        this.isSaving = true;

        this.store.dispatch(
            DashboardActions.updateEntityRowData({
                entityName: entityName,
                key: keyValue,
                rowData: payload,
            }),
        );

        // Subscribe to success/failure actions
        const actionSubscription = this.actions$
            .pipe(
                filter((action: any) => action.type === DashboardActions.updateEntityRowDataSuccess.type || action.type === DashboardActions.updateEntityRowDataFailure.type),
                take(1),
            )
            .subscribe((action: any) => {
                this.isSaving = false;

                if (action.type === DashboardActions.updateEntityRowDataFailure.type) {
                    // Handling error
                } else {
                    this.alertService.notify(AlertType.Success, 'Row updated successfully', 'Update Success');
                    // Update original data to reflect saved state
                    this.originalData = JSON.parse(JSON.stringify(this.editedData));
                    this.hasChanges = false;
                    // Update the row data in store
                    this.rowData = JSON.parse(JSON.stringify(this.editedData));
                }
                this.cdr.detectChanges();
                actionSubscription.unsubscribe();
            });
    }

    private getChangedFieldsPayload(): any {
        if (!this.selectedEntity || !this.originalData || !this.editedData) {
            return {};
        }
        const keyField = this.selectedEntity.key;
        const payload: any = {};
        Object.keys(this.editedData).forEach((field) => {
            if (field === keyField) return;
            if (this.editedData[field] !== this.originalData[field]) {
                const property = this.getPropertyByName(field);
                if (property) {
                    payload[field] = this.convertValueByType(this.editedData[field], property.type, property.nullable);
                } else {
                    payload[field] = this.editedData[field];
                }
            }
        });
        return payload;
    }

    /**
     * Convert all row data values to proper types based on metadata
     * This ensures that when we send data to the API, types match metadata
     */
    private convertRowDataToProperTypes(rowData: any): any {
        if (!this.selectedEntity || !rowData) {
            return rowData;
        }

        const converted: any = {};

        // Convert each property based on its metadata type
        Object.keys(rowData).forEach((key) => {
            const property = this.getPropertyByName(key);
            if (property) {
                converted[key] = this.convertValueByType(rowData[key], property.type, property.nullable);
            } else {
                // If property not in metadata, keep as-is (shouldn't happen, but safety fallback)
                converted[key] = rowData[key];
            }
        });

        return converted;
    }
}
